import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { BuildingCreateRequest, BuildingDto, BuildingEditRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useAddBuilding = () => {
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BuildingCreateRequest) => api.postBuildingsV1(data),
    onSuccess() {
      showFlashToast({ message: t("page.buildings.new.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.buildings.new.notification.failed"), variant: "alert" });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BUILDING_LIST(projectId) });
    },
  });
};

const useUpdateBuilding = () => {
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { buildingId: string; data: BuildingEditRequest }) =>
      api.putBuildingsV1(payload.buildingId, payload.data),
    onSuccess() {
      showFlashToast({ message: t("page.buildings.edit.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.buildings.edit.notification.failed"), variant: "alert" });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BUILDING_LIST(projectId) });
    },
  });
};

const useDeleteBuilding = () => {
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (building: BuildingDto) => api.deleteBuildingsByIdV1(building.id),
    onSuccess() {
      showFlashToast({ variant: "success", message: t("page.buildings.actions.delete.notifications.success") });
    },
    onError() {
      showFlashToast({ variant: "alert", message: t("page.buildings.actions.delete.notifications.failed") });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BUILDING_LIST(projectId) });
    },
  });
};

export const buildingMutations = {
  useAddBuilding,
  useUpdateBuilding,
  useDeleteBuilding,
};
